import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Edit3, Copy, Trash2, ChevronRight, Settings, Layout, Zap, Globe,
  Users, FileText, Check, X, Eye, MoreVertical, ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TemplateManager() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateData, setDuplicateData] = useState({ new_slug: '', new_name: '' });
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, modulesRes] = await Promise.all([
        axios.get(`${API}/templates`),
        axios.get(`${API}/modules`)
      ]);
      setTemplates(templatesRes.data);
      setModules(modulesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    const newSlug = `custom_template_${Date.now()}`;
    try {
      const response = await axios.post(`${API}/templates`, {
        name: 'New Template',
        slug: newSlug,
        category: 'custom',
        description: 'Custom template',
        icon: 'building'
      });
      setTemplates([...templates, response.data]);
      setSelectedTemplate(response.data);
      setShowEditor(true);
      toast.success('Template created');
    } catch (error) {
      toast.error('Failed to create template');
    }
  };

  const handleUpdateTemplate = async (updates) => {
    if (!selectedTemplate) return;
    
    try {
      const response = await axios.put(`${API}/templates/${selectedTemplate.slug}`, updates);
      setTemplates(templates.map(t => t.slug === selectedTemplate.slug ? response.data : t));
      setSelectedTemplate(response.data);
      toast.success('Template updated');
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const handleDuplicateTemplate = async () => {
    if (!selectedTemplate || !duplicateData.new_slug || !duplicateData.new_name) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/templates/${selectedTemplate.slug}/duplicate?new_slug=${duplicateData.new_slug}&new_name=${encodeURIComponent(duplicateData.new_name)}`
      );
      setTemplates([...templates, response.data]);
      setShowDuplicateModal(false);
      setDuplicateData({ new_slug: '', new_name: '' });
      toast.success('Template duplicated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate template');
    }
  };

  const handleDeleteTemplate = async (slug) => {
    if (!window.confirm('Are you sure you want to archive this template?')) return;
    
    try {
      await axios.delete(`${API}/templates/${slug}`);
      setTemplates(templates.map(t => t.slug === slug ? { ...t, status: 'archived' } : t));
      if (selectedTemplate?.slug === slug) {
        setSelectedTemplate(null);
        setShowEditor(false);
      }
      toast.success('Template archived');
    } catch (error) {
      toast.error('Failed to archive template');
    }
  };

  const toggleModule = (moduleKey) => {
    if (!selectedTemplate) return;
    
    const currentModules = selectedTemplate.modules_enabled || [];
    const newModules = currentModules.includes(moduleKey)
      ? currentModules.filter(m => m !== moduleKey)
      : [...currentModules, moduleKey];
    
    handleUpdateTemplate({ modules_enabled: newModules });
  };

  if (loading) {
    return (
      <div className="template-manager">
        <div className="loading-spinner">Loading templates...</div>
      </div>
    );
  }

  return (
    <div className="template-manager" data-testid="template-manager">
      {/* Header */}
      <header className="manager-header">
        <div className="header-left">
          <button className="back-link" onClick={() => navigate('/')}>
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1>Template Manager</h1>
            <p>Manage vertical industry templates for DepinaCRM</p>
          </div>
        </div>
        <button className="create-btn" onClick={handleCreateTemplate} data-testid="create-template-btn">
          <Plus size={18} />
          Create Template
        </button>
      </header>

      <div className="manager-content">
        {/* Template List */}
        <aside className="template-list">
          <div className="list-header">
            <h3>Templates ({templates.filter(t => t.status !== 'archived').length})</h3>
          </div>
          <div className="list-items">
            {templates.filter(t => t.status !== 'archived').map(template => (
              <div
                key={template.slug}
                className={`template-item ${selectedTemplate?.slug === template.slug ? 'active' : ''}`}
                onClick={() => {
                  setSelectedTemplate(template);
                  setShowEditor(true);
                }}
                data-testid={`template-item-${template.slug}`}
              >
                <div className="item-icon" style={{ background: template.default_theme?.primary_color || '#8B7355' }}>
                  {template.name.charAt(0)}
                </div>
                <div className="item-info">
                  <strong>{template.name}</strong>
                  <span>{template.modules_enabled?.length || 0} modules</span>
                </div>
                <span className={`status-badge ${template.status}`}>{template.status}</span>
              </div>
            ))}
          </div>
        </aside>

        {/* Template Editor */}
        {showEditor && selectedTemplate ? (
          <main className="template-editor">
            <div className="editor-header">
              <div className="editor-title">
                <h2>{selectedTemplate.name}</h2>
                <span className="slug-badge">{selectedTemplate.slug}</span>
              </div>
              <div className="editor-actions">
                <button 
                  className="action-btn"
                  onClick={() => {
                    setDuplicateData({ new_slug: '', new_name: '' });
                    setShowDuplicateModal(true);
                  }}
                >
                  <Copy size={16} />
                  Duplicate
                </button>
                <button 
                  className="action-btn danger"
                  onClick={() => handleDeleteTemplate(selectedTemplate.slug)}
                >
                  <Trash2 size={16} />
                  Archive
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="editor-tabs">
              {['general', 'modules', 'navigation', 'dashboard', 'automations', 'website'].map(tab => (
                <button
                  key={tab}
                  className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            <div className="editor-content">
              {/* General Tab */}
              {activeTab === 'general' && (
                <div className="tab-panel">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Template Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={selectedTemplate.name}
                        onChange={(e) => handleUpdateTemplate({ name: e.target.value })}
                        data-testid="template-name-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Slug</label>
                      <input
                        type="text"
                        className="form-input"
                        value={selectedTemplate.slug}
                        disabled
                      />
                    </div>
                    <div className="form-group">
                      <label>Category</label>
                      <select
                        className="form-input"
                        value={selectedTemplate.category}
                        onChange={(e) => handleUpdateTemplate({ category: e.target.value })}
                      >
                        <option value="beauty">Beauty & Wellness</option>
                        <option value="healthcare">Healthcare</option>
                        <option value="childcare">Childcare</option>
                        <option value="professional_services">Professional Services</option>
                        <option value="real_estate">Real Estate</option>
                        <option value="trades">Trades</option>
                        <option value="technology">Technology</option>
                        <option value="services">Services</option>
                        <option value="fitness">Fitness</option>
                        <option value="custom">Custom</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Icon</label>
                      <select
                        className="form-input"
                        value={selectedTemplate.icon}
                        onChange={(e) => handleUpdateTemplate({ icon: e.target.value })}
                      >
                        <option value="scissors">Scissors (Salon)</option>
                        <option value="baby">Baby (Daycare)</option>
                        <option value="calculator">Calculator (Accounting)</option>
                        <option value="home">Home (Real Estate)</option>
                        <option value="zap">Zap (Electrical)</option>
                        <option value="monitor">Monitor (IT)</option>
                        <option value="sparkles">Sparkles (Cleaning)</option>
                        <option value="dumbbell">Dumbbell (Gym)</option>
                        <option value="smile">Smile (Dental)</option>
                        <option value="building">Building (Default)</option>
                      </select>
                    </div>
                    <div className="form-group full-width">
                      <label>Description</label>
                      <textarea
                        className="form-input"
                        value={selectedTemplate.description}
                        onChange={(e) => handleUpdateTemplate({ description: e.target.value })}
                        rows={3}
                      />
                    </div>
                  </div>
                  
                  <div className="theme-section">
                    <h4>Default Theme Colors</h4>
                    <div className="color-grid">
                      <div className="color-picker-group">
                        <label>Primary Color</label>
                        <div className="color-input-wrapper">
                          <input
                            type="color"
                            value={selectedTemplate.default_theme?.primary_color || '#8B7355'}
                            onChange={(e) => handleUpdateTemplate({
                              default_theme: { ...selectedTemplate.default_theme, primary_color: e.target.value }
                            })}
                          />
                          <input
                            type="text"
                            className="form-input"
                            value={selectedTemplate.default_theme?.primary_color || '#8B7355'}
                            onChange={(e) => handleUpdateTemplate({
                              default_theme: { ...selectedTemplate.default_theme, primary_color: e.target.value }
                            })}
                          />
                        </div>
                      </div>
                      <div className="color-picker-group">
                        <label>Accent Color</label>
                        <div className="color-input-wrapper">
                          <input
                            type="color"
                            value={selectedTemplate.default_theme?.accent_color || '#5A9A5A'}
                            onChange={(e) => handleUpdateTemplate({
                              default_theme: { ...selectedTemplate.default_theme, accent_color: e.target.value }
                            })}
                          />
                          <input
                            type="text"
                            className="form-input"
                            value={selectedTemplate.default_theme?.accent_color || '#5A9A5A'}
                            onChange={(e) => handleUpdateTemplate({
                              default_theme: { ...selectedTemplate.default_theme, accent_color: e.target.value }
                            })}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Modules Tab */}
              {activeTab === 'modules' && (
                <div className="tab-panel">
                  <p className="tab-description">
                    Select which modules are enabled by default for this template.
                  </p>
                  <div className="modules-grid">
                    {Object.entries(modules).map(([key, mod]) => {
                      const isEnabled = selectedTemplate.modules_enabled?.includes(key);
                      return (
                        <div
                          key={key}
                          className={`module-card ${isEnabled ? 'enabled' : ''}`}
                          onClick={() => toggleModule(key)}
                          data-testid={`module-${key}`}
                        >
                          <div className="module-header">
                            <div className="module-check">
                              {isEnabled ? <Check size={16} /> : null}
                            </div>
                            <h4>{mod.name}</h4>
                          </div>
                          <p>{mod.description}</p>
                          <div className="module-features">
                            {mod.features?.slice(0, 3).map(f => (
                              <span key={f}>{f}</span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Navigation Tab */}
              {activeTab === 'navigation' && (
                <div className="tab-panel">
                  <p className="tab-description">
                    Configure the default navigation menu for this template.
                  </p>
                  <div className="nav-editor">
                    {selectedTemplate.default_navigation?.map((item, index) => (
                      <div key={index} className="nav-item-editor">
                        <input
                          type="text"
                          className="form-input"
                          value={item.label}
                          placeholder="Label"
                          onChange={(e) => {
                            const newNav = [...selectedTemplate.default_navigation];
                            newNav[index] = { ...item, label: e.target.value };
                            handleUpdateTemplate({ default_navigation: newNav });
                          }}
                        />
                        <input
                          type="text"
                          className="form-input"
                          value={item.path}
                          placeholder="/path"
                          onChange={(e) => {
                            const newNav = [...selectedTemplate.default_navigation];
                            newNav[index] = { ...item, path: e.target.value };
                            handleUpdateTemplate({ default_navigation: newNav });
                          }}
                        />
                        <input
                          type="text"
                          className="form-input"
                          value={item.icon}
                          placeholder="icon"
                          onChange={(e) => {
                            const newNav = [...selectedTemplate.default_navigation];
                            newNav[index] = { ...item, icon: e.target.value };
                            handleUpdateTemplate({ default_navigation: newNav });
                          }}
                        />
                        <button
                          className="remove-btn"
                          onClick={() => {
                            const newNav = selectedTemplate.default_navigation.filter((_, i) => i !== index);
                            handleUpdateTemplate({ default_navigation: newNav });
                          }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                    <button
                      className="add-nav-btn"
                      onClick={() => {
                        const newNav = [...(selectedTemplate.default_navigation || []), {
                          key: `nav_${Date.now()}`,
                          label: 'New Item',
                          icon: 'circle',
                          path: '/new'
                        }];
                        handleUpdateTemplate({ default_navigation: newNav });
                      }}
                    >
                      <Plus size={16} /> Add Navigation Item
                    </button>
                  </div>
                </div>
              )}

              {/* Dashboard Tab */}
              {activeTab === 'dashboard' && (
                <div className="tab-panel">
                  <p className="tab-description">
                    Configure the default dashboard widgets for this template.
                  </p>
                  <div className="dashboard-editor">
                    {selectedTemplate.default_dashboard?.map((widget, index) => (
                      <div key={index} className="widget-editor">
                        <select
                          className="form-input"
                          value={widget.widget}
                          onChange={(e) => {
                            const newDash = [...selectedTemplate.default_dashboard];
                            newDash[index] = { ...widget, widget: e.target.value };
                            handleUpdateTemplate({ default_dashboard: newDash });
                          }}
                        >
                          <option value="stat">Stat Card</option>
                          <option value="chart">Chart</option>
                          <option value="list">List</option>
                          <option value="calendar">Calendar</option>
                        </select>
                        <input
                          type="text"
                          className="form-input"
                          value={widget.title}
                          placeholder="Widget Title"
                          onChange={(e) => {
                            const newDash = [...selectedTemplate.default_dashboard];
                            newDash[index] = { ...widget, title: e.target.value };
                            handleUpdateTemplate({ default_dashboard: newDash });
                          }}
                        />
                        <input
                          type="text"
                          className="form-input"
                          value={widget.key}
                          placeholder="data_key"
                          onChange={(e) => {
                            const newDash = [...selectedTemplate.default_dashboard];
                            newDash[index] = { ...widget, key: e.target.value };
                            handleUpdateTemplate({ default_dashboard: newDash });
                          }}
                        />
                        <button
                          className="remove-btn"
                          onClick={() => {
                            const newDash = selectedTemplate.default_dashboard.filter((_, i) => i !== index);
                            handleUpdateTemplate({ default_dashboard: newDash });
                          }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                    <button
                      className="add-nav-btn"
                      onClick={() => {
                        const newDash = [...(selectedTemplate.default_dashboard || []), {
                          widget: 'stat',
                          key: `widget_${Date.now()}`,
                          title: 'New Widget',
                          icon: 'activity'
                        }];
                        handleUpdateTemplate({ default_dashboard: newDash });
                      }}
                    >
                      <Plus size={16} /> Add Dashboard Widget
                    </button>
                  </div>
                </div>
              )}

              {/* Automations Tab */}
              {activeTab === 'automations' && (
                <div className="tab-panel">
                  <p className="tab-description">
                    Configure default automation workflows for this template.
                  </p>
                  <div className="automations-editor">
                    {selectedTemplate.default_automations?.map((auto, index) => (
                      <div key={index} className="automation-item">
                        <input
                          type="text"
                          className="form-input"
                          value={auto.name}
                          placeholder="Automation Name"
                          onChange={(e) => {
                            const newAuto = [...selectedTemplate.default_automations];
                            newAuto[index] = { ...auto, name: e.target.value };
                            handleUpdateTemplate({ default_automations: newAuto });
                          }}
                        />
                        <select
                          className="form-input"
                          value={auto.trigger}
                          onChange={(e) => {
                            const newAuto = [...selectedTemplate.default_automations];
                            newAuto[index] = { ...auto, trigger: e.target.value };
                            handleUpdateTemplate({ default_automations: newAuto });
                          }}
                        >
                          <option value="appointment_booked">Appointment Booked</option>
                          <option value="appointment_reminder">Appointment Reminder</option>
                          <option value="appointment_completed">Appointment Completed</option>
                          <option value="new_lead">New Lead</option>
                          <option value="form_submission">Form Submitted</option>
                          <option value="client_birthday">Client Birthday</option>
                          <option value="invoice_due">Invoice Due</option>
                          <option value="manual">Manual</option>
                        </select>
                        <select
                          className="form-input"
                          value={auto.action}
                          onChange={(e) => {
                            const newAuto = [...selectedTemplate.default_automations];
                            newAuto[index] = { ...auto, action: e.target.value };
                            handleUpdateTemplate({ default_automations: newAuto });
                          }}
                        >
                          <option value="send_email">Send Email</option>
                          <option value="send_sms">Send SMS</option>
                          <option value="send_notification">Send Notification</option>
                          <option value="create_task">Create Task</option>
                        </select>
                        <button
                          className="remove-btn"
                          onClick={() => {
                            const newAuto = selectedTemplate.default_automations.filter((_, i) => i !== index);
                            handleUpdateTemplate({ default_automations: newAuto });
                          }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                    <button
                      className="add-nav-btn"
                      onClick={() => {
                        const newAuto = [...(selectedTemplate.default_automations || []), {
                          name: 'New Automation',
                          trigger: 'manual',
                          action: 'send_notification'
                        }];
                        handleUpdateTemplate({ default_automations: newAuto });
                      }}
                    >
                      <Plus size={16} /> Add Automation
                    </button>
                  </div>
                </div>
              )}

              {/* Website Tab */}
              {activeTab === 'website' && (
                <div className="tab-panel">
                  <p className="tab-description">
                    Configure the default website template settings.
                  </p>
                  <div className="website-settings">
                    <div className="form-group">
                      <label>Hero Heading</label>
                      <input
                        type="text"
                        className="form-input"
                        value={selectedTemplate.default_website_template?.hero_heading || ''}
                        placeholder="Use {business_name} for dynamic name"
                        onChange={(e) => handleUpdateTemplate({
                          default_website_template: {
                            ...selectedTemplate.default_website_template,
                            hero_heading: e.target.value
                          }
                        })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Hero Subheading</label>
                      <input
                        type="text"
                        className="form-input"
                        value={selectedTemplate.default_website_template?.hero_subheading || ''}
                        onChange={(e) => handleUpdateTemplate({
                          default_website_template: {
                            ...selectedTemplate.default_website_template,
                            hero_subheading: e.target.value
                          }
                        })}
                      />
                    </div>
                    <div className="form-group">
                      <label>CTA Button Text</label>
                      <input
                        type="text"
                        className="form-input"
                        value={selectedTemplate.default_website_template?.cta_text || ''}
                        onChange={(e) => handleUpdateTemplate({
                          default_website_template: {
                            ...selectedTemplate.default_website_template,
                            cta_text: e.target.value
                          }
                        })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Default Pages (comma-separated)</label>
                      <input
                        type="text"
                        className="form-input"
                        value={selectedTemplate.default_website_template?.pages?.join(', ') || ''}
                        placeholder="home, services, about, contact"
                        onChange={(e) => handleUpdateTemplate({
                          default_website_template: {
                            ...selectedTemplate.default_website_template,
                            pages: e.target.value.split(',').map(p => p.trim()).filter(Boolean)
                          }
                        })}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </main>
        ) : (
          <main className="template-editor empty">
            <div className="empty-state">
              <Settings size={48} />
              <h3>Select a Template</h3>
              <p>Choose a template from the list to edit its configuration</p>
            </div>
          </main>
        )}
      </div>

      {/* Duplicate Modal */}
      {showDuplicateModal && (
        <div className="modal-overlay" onClick={() => setShowDuplicateModal(false)}>
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Duplicate Template</h3>
              <button className="modal-close" onClick={() => setShowDuplicateModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>New Template Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={duplicateData.new_name}
                  onChange={(e) => setDuplicateData(prev => ({ ...prev, new_name: e.target.value }))}
                  placeholder="e.g., Nail Salon"
                />
              </div>
              <div className="form-group">
                <label>New Slug</label>
                <input
                  type="text"
                  className="form-input"
                  value={duplicateData.new_slug}
                  onChange={(e) => setDuplicateData(prev => ({ 
                    ...prev, 
                    new_slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')
                  }))}
                  placeholder="e.g., nail_salon"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="toolbar-btn" onClick={() => setShowDuplicateModal(false)}>
                Cancel
              </button>
              <button className="toolbar-btn primary" onClick={handleDuplicateTemplate}>
                Duplicate Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
